/**
 * CoachPlaybook - Roster Picker Component
 * Version: 0.3.0
 * Description: Modal for selecting players from roster to assign to positions
 */

import React, { useState, useEffect, useMemo } from 'react';
import { getAll, STORES } from '../../utils/db';

const RosterPicker = ({
  isOpen,
  onClose,
  onSelectPlayer,
  currentPosition,
  excludePlayerIds = [],
  title = 'Select Player'
}) => {
  const [players, setPlayers] = useState([]);
  const [search, setSearch] = useState('');
  const [filterPosition, setFilterPosition] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // Load players
  useEffect(() => {
    if (isOpen) {
      loadPlayers();
    }
  }, [isOpen]);
  
  const loadPlayers = async () => {
    setIsLoading(true);
    try {
      const allPlayers = await getAll(STORES.PLAYERS);
      setPlayers(allPlayers || []);
    } catch (error) {
      console.error('Error loading players:', error);
      setPlayers([]);
    }
    setIsLoading(false);
  };
  
  // Filter players
  const filteredPlayers = useMemo(() => {
    let filtered = players.filter(p => !excludePlayerIds.includes(p.id));
    
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(p =>
        p.firstName?.toLowerCase().includes(searchLower) ||
        p.lastName?.toLowerCase().includes(searchLower) ||
        p.jerseyNumber?.toString().includes(search)
      );
    }
    
    if (filterPosition) {
      filtered = filtered.filter(p => p.position === filterPosition);
    }
    
    return filtered;
  }, [players, search, filterPosition, excludePlayerIds]);
  
  if (!isOpen) return null;
  
  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <h3 style={styles.title}>{title}</h3>
          {currentPosition && (
            <span style={{
              ...styles.positionBadge,
              backgroundColor: currentPosition.color
            }}>
              {currentPosition.positionLabel}
            </span>
          )}
          <button style={styles.closeButton} onClick={onClose}>✕</button>
        </div>
        
        {/* Search & Filter */}
        <div style={styles.searchRow}>
          <input
            type="text"
            placeholder="Search by name or number..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={styles.searchInput}
            autoFocus
          />
          <select
            value={filterPosition}
            onChange={e => setFilterPosition(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="">All Positions</option>
            <option value="PG">Point Guard</option>
            <option value="SG">Shooting Guard</option>
            <option value="SF">Small Forward</option>
            <option value="PF">Power Forward</option>
            <option value="C">Center</option>
          </select>
        </div>
        
        {/* Players List */}
        <div style={styles.playersList}>
          {isLoading ? (
            <div style={styles.emptyState}>Loading players...</div>
          ) : filteredPlayers.length === 0 ? (
            <div style={styles.emptyState}>
              {players.length === 0 
                ? 'No players in roster. Add players in Team Roster first.'
                : 'No players match your search.'
              }
            </div>
          ) : (
            filteredPlayers.map(player => (
              <div
                key={player.id}
                style={styles.playerItem}
                onClick={() => {
                  onSelectPlayer(player);
                  onClose();
                }}
              >
                {/* Player Avatar/Number */}
                {player.avatar?.thumbnail ? (
                  <img 
                    src={player.avatar.thumbnail} 
                    alt={`${player.firstName} ${player.lastName}`}
                    style={styles.playerAvatar}
                  />
                ) : (
                  <div style={styles.playerNumberCircle}>
                    {player.jerseyNumber}
                  </div>
                )}
                
                {/* Player Info */}
                <div style={styles.playerInfo}>
                  <span style={styles.playerName}>
                    {player.firstName} {player.lastName}
                  </span>
                  <span style={styles.playerDetails}>
                    #{player.jerseyNumber} • {player.position || 'No Position'}
                  </span>
                </div>
                
                {/* Position Match Indicator */}
                {currentPosition && player.position === currentPosition.positionLabel && (
                  <span style={styles.matchBadge}>✓ Match</span>
                )}
              </div>
            ))
          )}
        </div>
        
        {/* Footer */}
        <div style={styles.footer}>
          <span style={styles.footerText}>
            {filteredPlayers.length} player{filteredPlayers.length !== 1 ? 's' : ''} available
          </span>
          <button style={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '16px'
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '400px',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 20px',
    borderBottom: '1px solid #e5e7eb'
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#1f2937',
    flex: 1
  },
  positionBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    color: '#fff',
    fontSize: '12px',
    fontWeight: '600'
  },
  closeButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#f3f4f6',
    cursor: 'pointer',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  searchRow: {
    display: 'flex',
    gap: '8px',
    padding: '12px 16px',
    borderBottom: '1px solid #e5e7eb'
  },
  searchInput: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    outline: 'none'
  },
  filterSelect: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    outline: 'none',
    backgroundColor: '#fff'
  },
  playersList: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#6b7280',
    fontSize: '14px'
  },
  playerItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  playerAvatar: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    objectFit: 'cover'
  },
  playerNumberCircle: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    backgroundColor: '#3b82f6',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '16px'
  },
  playerInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  playerName: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1f2937'
  },
  playerDetails: {
    fontSize: '13px',
    color: '#6b7280'
  },
  matchBadge: {
    padding: '4px 8px',
    borderRadius: '4px',
    backgroundColor: '#dcfce7',
    color: '#166534',
    fontSize: '12px',
    fontWeight: '500'
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderTop: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb'
  },
  footerText: {
    fontSize: '13px',
    color: '#6b7280'
  },
  cancelButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    backgroundColor: '#fff',
    color: '#374151',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  }
};

// Add hover effect via CSS
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  .roster-picker-item:hover {
    background-color: #f3f4f6 !important;
  }
`;
if (typeof document !== 'undefined') {
  document.head.appendChild(styleSheet);
}

export default RosterPicker;
